<template>
  <main
    class="recipes"
    data-jest="main"
  >
    <div
      v-if="showTitle || showTopPaginator"
      class="recipes__top"
    >
      <h1
        v-if="showTitle"
        class="recipes__title"
        data-jest="title"
      >
        {{ page.title }}
      </h1>
      <ThePaginator
        v-if="showTopPaginator"
        :active-page="+activePage"
        :is-trailing-slash="true"
        :route-beginning="currentPath"
        :total-pages-count="page.total"
        class="recipes__paginator recipes__paginator_top"
        page-prefix="page/"
        @page-link-click="sendPaginatorAnalytics"
      />
    </div>

    <div class="recipes__list">
      <template
        v-for="(item, index) of recipesAndWidgets"
        :key="index"
      >
        <template
          v-for="(recipe, recipeIndex) of item.first"
          :key="recipe.id"
        >
          <RecipeCard
            :comments-count="recipe.commentsCount"
            :comments-enabled="recipe.commentsEnabled"
            :cooking-time="recipe.totalTime"
            :cover-loading="
              index === 0 && recipeIndex === 0
                ? 'eager'
                : index === 0 && recipeIndex === 1
                  ? 'auto'
                  : 'lazy'
            "
            :covers="recipe.image"
            :dislikes="reactionsStore.getDislikes(recipe.id)"
            :favorite="articlesStore.getArticleFavorite(recipe.id)"
            :folders="foldersStore.$state"
            :event-position="eventPosition"
            :is-cooking-time-visible="true"
            :likes="reactionsStore.getLikes(recipe.id)"
            :link-to-edit="getEditLinkHref(recipe.id)"
            :open-comments-in-new-tab="true"
            :reaction-state="reactionsStore.getReactionsState(recipe.id)"
            :show-admin-controls-panel="indexStore.isAuthenticatedInWordpress"
            :title="recipe.title"
            :url="recipe.url"
            :user="user"
            :views-count="recipe.views"
            :views-loading="getLoadingViews(recipe.id)"
            class="recipes__card"
            @update-like="onUpdateLike(recipe)"
            @update-dislike="onUpdateDislike(recipe)"
            @click-on-restore-button="updateViewsCount(recipe.id)"
            @favorite-delete="
              (favorite) => {
                onFavoriteDelete(favorite, {
                  id: recipe.id,
                  title: recipe.title,
                  url: recipe.url,
                });
              }
            "
            @favorite-create-folder-and-add="
              (nameFolder) =>
                onFavoriteCreateFolderAndAdd(nameFolder, {
                  id: recipe.id,
                  title: recipe.title,
                  url: recipe.url,
                })
            "
            @favorite-create-folder-and-transfer="
              (nameFolder, favorite) =>
                onFavoriteCreateFolderAndTransfer(
                  nameFolder,
                  favorite,
                  {
                    id: recipe.id,
                    title: recipe.title,
                    url: recipe.url,
                  },
                  false,
                )
            "
            @favorite-transfer-to-folder="
              (favorite, folder) =>
                onFavoriteTransferToFolder(
                  favorite,
                  folder,
                  {
                    id: recipe.id,
                    title: recipe.title,
                    url: recipe.url,
                  },
                  false,
                )
            "
            @favorite-add-to-folder="
              (folder) =>
                onFavoriteAddToFolder(folder, {
                  id: recipe.id,
                  title: recipe.title,
                  url: recipe.url,
                })
            "
            @favorite-deferred="onFavoriteDeferred"
            @to-comments="onToComments(recipe.url)"
            @click-title="clickCard(recipe.url, recipe.title)"
            @click-picture="clickCard(recipe.url, recipe.title)"
          />
        </template>

        <WidgetsRendererContainer
          v-if="item.second.length"
          :key="index"
          :widgets="item.second"
          area="В_ленте_рецептов"
          recipes-widget-display-type="big-tiles"
        />
      </template>
    </div>

    <BaseAutoLoading
      v-if="autoLoadingEnabled"
      :fetching="status === 'pending'"
      @load-new-items="onLoadNewItems"
    >
      <template #placeholder>
        <RecipeCardPlaceholder
          v-for="n in 3"
          :key="n"
        />
      </template>
    </BaseAutoLoading>

    <ThePaginator
      v-if="showBottomPaginator"
      :active-page="+activePage"
      :is-trailing-slash="true"
      :route-beginning="currentPath"
      :total-pages-count="page.total"
      class="recipes__paginator recipes__paginator_bottom"
      page-prefix="page/"
      @page-link-click="sendPaginatorAnalytics"
    />

    <a
      v-if="showMoreButtonVisible"
      :href="showMoreLink"
      class="recipes__show-more"
      data-jest="show-more"
      @click="showMoreAnalytics"
    >
      Показать ещё
    </a>
  </main>
</template>

<script lang="ts" setup>
  import type { PropType } from 'vue';

  import RecipeCard from '~/components/RecipeCard/RecipeCard.vue';
  import WidgetsRendererContainer from '~/containers/WidgetsRendererContainer.vue';
  import RecipeCardPlaceholder from '~/placeholders/RecipeCardPlaceholder/RecipeCardPlaceholder.vue';
  import BaseAutoLoading from '~/components/BaseAutoLoading/BaseAutoLoading.vue';

  import { getEditLinkHref, getFullUrl } from '~/utils';
  import { getMixedItemsArray } from '~/utils/arrays';
  import { isRecipesMainPage, isRecipesPage } from '~/utils/router';
  import ThePaginator from '@/components/lh-ui/ThePaginator/ThePaginator.vue';
  import { useReactionsStore } from '~/store/reactions';
  import { useIndexStore } from '~/store';
  import { useFoldersStore } from '~/store/folders';
  import { useArticlesStore } from '~/store/articles';
  import { useWidgetsStore } from '~/store/widgets';
  import { useFavorite } from '~/composables/useFavoriteMethods';
  import { useUpdatingViewsCount } from '~/composables/useUpdatingViewsCount';
  import { RecipeArchiveSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
  import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';
  import { useRecipeAnalyticsHead } from '~/domains/head/analytics/services/useRecipeAnalyticsHead';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';

  provide('eventName', 'Главная');

  definePageMeta({
    layout: 'recipes',
  });

  const props = defineProps({
    slug: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    activePage: {
      type: String as PropType<string>,
      default: '1',
    },
  });

  const {
    onFavoriteDelete,
    onFavoriteDeferred,
    onFavoriteAddToFolder,
    onFavoriteTransferToFolder,
    onFavoriteCreateFolderAndAdd,
    onFavoriteCreateFolderAndTransfer,
  } = useFavorite();
  const { getLoadingViews, updateViewsCount } = useUpdatingViewsCount();
  const articlesStore = useArticlesStore();
  const foldersStore = useFoldersStore();
  const indexStore = useIndexStore();
  const nuxtApp = useNuxtApp();
  const reactionsStore = useReactionsStore();
  const route = useRoute();
  const widgetsStore = useWidgetsStore();

  const recipes = ref<Recipe[]>([]);
  const page = ref<{ title: string; total: number }>({ title: '', total: 1 });
  const currentPath = computed(() => `/recipes/${(props.slug || []).join('/')}`);
  const user = computed(() => indexStore.user);
  const isHomePage = computed(() => isRecipesMainPage(route.name));
  const eventPosition = computed(() => (isHomePage.value ? 'recipe-main' : 'recipe-filters'));

  const showBottomPaginator = computed(() => !isHomePage.value && isRecipesPage(route.name));
  const showTopPaginator = computed(
    () => !isHomePage.value && +props.activePage > 1 && isRecipesPage(route.name),
  );
  const showTitle = computed(() =>
    Boolean(page.value.title && (!isHomePage.value || (isHomePage.value && +props.activePage > 1))),
  );
  const betweenRecipesWidgets = computed(() => widgetsStore.currentBetweenRecipes);
  const recipesAndWidgets = computed(() => {
    // смешанные статьи и виджеты
    return getMixedItemsArray(recipes.value, betweenRecipesWidgets.value, 5, 1, false);
  });
  const autoLoadingEnabled = computed(
    () => isHomePage.value && autoLoading.value.page <= autoLoading.value.count,
  );
  // Отображать кнопку показать еще?
  const showMoreButtonVisible = computed(
    () => isHomePage.value && !autoLoadingEnabled.value && status.value !== 'pending',
  );
  // ссылка на страницу, которую нужно открыть после всех автопрдгрузок при клике на ссылку внизу страницы
  const showMoreLink = computed(() => `/recipes/page/${autoLoading.value.count + 2}/`);

  const autoLoading = ref({
    fetching: false as boolean,
    page: +props.activePage,
    // количество автоподгрузок
    count: 3,
  });

  const {
    data: responseData,
    status,
    error: fetchError,
    refresh: loadNewItems,
  } = await useAsyncData(`recipes_${currentPath.value}_${autoLoading.value.page}`, () => {
    const api = nuxtApp.$recipesApi;

    return props.slug?.length
      ? api.fetchRecipesQuery(+autoLoading.value.page, currentPath.value)
      : api.fetchHomeRecipes(+autoLoading.value.page);
  });

  if (fetchError.value) throw createError(fetchError.value);

  watchEffect(() => {
    const status = responseData?.value?.data?.status;

    if ((status && status === 503) || status === 404) {
      throw createError({
        statusCode: status,
        message: `Error in Recipes: ${responseData?.value?.message}`,
      });
    } else if (responseData.value) {
      recipes.value.push(...responseData.value.items);
      page.value.title = responseData.value?.meta.title ?? '';
      page.value.total = responseData.value?.meta.totalPages ?? 1;
    }
  });

  const recipesIds = computed(() => recipes.value.map((item) => item.id));
  watch(
    recipesIds,
    () => {
      reactionsStore.fetchReactions(recipesIds.value);
    },
    { immediate: true },
  );

  watch(
    user,
    () => {
      articlesStore.fetchAndSetFavorites({ post_ids: recipesIds.value.join(',') });
    },
    { immediate: true },
  );

  const seo = parseSeoOrThrow(RecipeArchiveSeoInputSchema, responseData.value?.seo);
  useSeoHead(seo);
  useRecipeAnalyticsHead(seo);

  const onLoadNewItems = async (): Promise<void> => {
    autoLoading.value.fetching = true;
    autoLoading.value.page += 1;

    await loadNewItems();

    autoLoading.value.fetching = false;
  };

  const onUpdateLike = async (recipe: Recipe): Promise<void> => {
    await reactionsStore.updateLike(recipe.id);
  };

  const onUpdateDislike = async (recipe: Recipe): Promise<void> => {
    await reactionsStore.updateDislike(recipe.id);
  };

  const onToComments = (url: string): void => {
    nuxtApp.$sendYandexMetrika({
      level1: 'Комментарии_переход к комментариям',
      level4: getFullUrl(url),
      level6: 'Рецепты',
    });
  };

  const clickCard = (url: string, title: string): void => {
    nuxtApp.$sendYandexMetrika({
      level1: 'Выбор_материала',
      level4: url,
      level6: title,
    });
  };

  const sendPaginatorAnalytics = (page: number, link: string): void => {
    nuxtApp.$sendYandexMetrika({
      level1: 'Пагинация_главная',
      level4: getFullUrl(link),
      level5: page,
    });
  };

  const showMoreAnalytics = (): void => {
    nuxtApp.$sendYandexMetrika({
      level1: 'Клик_показать_еще',
      level4: getFullUrl(showMoreLink.value),
      level6: 'Показать ещё',
    });
  };

  watch(
    user,
    () => {
      articlesStore.fetchAndSetFavorites({ post_ids: recipesIds.value.join(',') });
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .recipes {
    &__card {
      margin-bottom: 16px;

      @include tablet {
        margin-bottom: 24px;
      }
    }
    &__top {
      background-color: white;
      padding-bottom: 24px;
    }
    &__title {
      @include padding-x(16px);
      @include fontH3(true);

      & + .recipes__paginator {
        margin-top: 18px;
      }

      @include tablet {
        @include padding-x(0);
        @include fontH2();
      }
    }
    &__paginator {
      background-color: transparent;
      padding-left: 10px;
      padding-right: 10px;

      @include tablet {
        padding-left: 0;
        padding-right: 0;
      }

      &_bottom {
        margin-bottom: 26px;

        @include tablet {
          margin-bottom: 38px;
        }
      }

      &_top {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
    &__show-more {
      @include fontTextBtn;

      background: $main;
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      width: auto;
      height: 48px;
      cursor: pointer;
      text-decoration: none;

      @include margin-x(16px);

      @include hover {
        background: $main-dark;
      }

      margin-bottom: 16px;
      @include tablet {
        margin-bottom: 24px;

        @include margin-x(0);
      }
    }
    &__list {
      max-width: 100vw;
      :deep() {
        .add-margin-bottom {
          margin-bottom: 16px;
          @include tablet {
            margin-bottom: 24px;
          }
        }
      }
    }

    :deep() {
      .lightgrey-box {
        margin: 0;

        @include tablet {
          margin: 24px -24px;
        }
      }
    }
  }
</style>
